import React, { useState } from 'react';
import Navbar from '../../components/navigation/Navbar';
import {
  Clock,
  Search,
  Filter,
  Download,
  ChevronDown,
  User,
  Shield,
  Eye,
  Edit,
  Trash2
} from 'lucide-react';

export default function AuditLogs() {
  const [search, setSearch] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  const auditLogs = [
    {
      id: 1,
      timestamp: '2024-02-12 14:32:45',
      user: 'admin@example.com',
      action: 'Search',
      target: 'License Plate: ABC-1234',
      details: 'Searched for license plate ABC-1234',
      status: 'success'
    },
    {
      id: 2,
      timestamp: '2024-02-12 14:25:12',
      user: 'officer_john@example.com',
      action: 'Added to Hot List',
      target: 'XYZ-5678',
      details: 'Added plate to hot list - Stolen Vehicle',
      status: 'success'
    },
    {
      id: 3,
      timestamp: '2024-02-12 14:18:33',
      user: 'system',
      action: 'Detection',
      target: 'LMN-9012',
      details: 'ALPR detection at Downtown Zone A',
      status: 'success'
    },
    {
      id: 4,
      timestamp: '2024-02-12 14:10:22',
      user: 'analyst_sarah@example.com',
      action: 'Exported Report',
      target: 'Statistics Report',
      details: 'Exported daily statistics report',
      status: 'success'
    },
    {
      id: 5,
      timestamp: '2024-02-12 13:55:11',
      user: 'admin@example.com',
      action: 'Modified Hot List',
      target: 'ABC-1234',
      details: 'Updated priority from Medium to High',
      status: 'success'
    },
    {
      id: 6,
      timestamp: '2024-02-12 13:42:05',
      user: 'officer_mike@example.com',
      action: 'Search',
      target: 'By Region: Downtown',
      details: 'Regional search for downtown area',
      status: 'success'
    },
  ];

  const getActionColor = (action) => {
    const colors = {
      'Search': 'bg-blue-100 text-blue-700',
      'Added to Hot List': 'bg-red-100 text-red-700',
      'Detection': 'bg-green-100 text-green-700',
      'Exported Report': 'bg-purple-100 text-purple-700',
      'Modified Hot List': 'bg-yellow-100 text-yellow-700',
    };
    return colors[action] || 'bg-gray-100 text-gray-700';
  };

  const getActionIcon = (action) => {
    const icons = {
      'Search': Eye,
      'Added to Hot List': Shield,
      'Detection': Clock,
      'Exported Report': Download,
      'Modified Hot List': Edit,
    };
    return icons[action] || Search;
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Audit Logs</h1>
              <p className="text-gray-600 mt-2">Track system activities and user actions</p>
            </div>
            <button className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold py-2.5 px-4 rounded-lg transition flex items-center gap-2">
              <Download size={18} />
              Export Logs
            </button>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex gap-4 items-end flex-wrap">
              <div className="flex-1 min-w-64">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Search
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 text-gray-400" size={18} />
                  <input
                    type="text"
                    placeholder="Search by user, action, or target..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                  />
                </div>
              </div>

              <div className="min-w-48">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  Action Type
                </label>
                <select
                  value={filterAction}
                  onChange={(e) => setFilterAction(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                >
                  <option value="all">All Actions</option>
                  <option value="search">Search</option>
                  <option value="hotlist">Hot List</option>
                  <option value="detection">Detection</option>
                  <option value="export">Export</option>
                </select>
              </div>

              <div className="min-w-48">
                <label className="block text-xs font-semibold text-gray-700 mb-2">
                  User
                </label>
                <select
                  value={filterUser}
                  onChange={(e) => setFilterUser(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none bg-white"
                >
                  <option value="all">All Users</option>
                  <option value="admin">Administrators</option>
                  <option value="officer">Officers</option>
                  <option value="analyst">Analysts</option>
                  <option value="system">System</option>
                </select>
              </div>
            </div>
          </div>

          {/* Logs Table */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                      Timestamp
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                      Action
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                      Target
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900">
                      Details
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {auditLogs.map((log) => {
                    const ActionIcon = getActionIcon(log.action);
                    return (
                      <tr key={log.id} className="hover:bg-gray-50 transition">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <Clock className="text-gray-400" size={16} />
                            <span className="text-sm text-gray-600 font-mono">{log.timestamp}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center">
                              <User size={16} className="text-emerald-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {log.user === 'system' ? 'System' : log.user.split('@')[0]}
                              </p>
                              <p className="text-xs text-gray-500">{log.user}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold ${getActionColor(log.action)}`}>
                            <ActionIcon size={14} />
                            {log.action}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <code className="text-xs bg-gray-100 text-gray-800 px-2 py-1 rounded">
                            {log.target}
                          </code>
                        </td>
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">{log.details}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Pagination */}
          <div className="mt-6 flex items-center justify-between">
            <p className="text-sm text-gray-600">Showing 1 to 6 of 2,547 logs</p>
            <div className="flex gap-2">
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                Previous
              </button>
              <div className="flex gap-1">
                {[1, 2, 3, '...', 425].map((page, idx) => (
                  <button
                    key={idx}
                    className={`w-10 h-10 rounded-lg font-medium transition ${
                      page === 1
                        ? 'bg-emerald-600 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    } ${page === '...' ? 'cursor-default' : ''}`}
                    disabled={page === '...'}
                  >
                    {page}
                  </button>
                ))}
              </div>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}